import React, { useState, useEffect, useMemo, useTransition, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink } from "react-router-dom";
import "./PhotoGallery.css";

// STEP 1: Define the 8 specific images for the Home Page section.
const homeImageData = [
  {
    src: "assets/images/event-banner/meetup-2026-img1.png",
    description: "",
    event: "Annual Meet 2026",
  },
  {
    src: "assets/images/gallery/year-2026/11.webp",
    description: "",
    event: "Annual Meet 2026",
  },
  {
    src: "assets/images/gallery/year-2026/Registration/10.webp",
    description: "    ",
    event: "Annual Meet 2026",
  },
  {
    src: "assets/images/gallery/year-2026/Keynote-speaker/1.webp",
    description: "",
    event: "Annual Meet 2026",
  },
  {
    src: "assets/images/gallery/year-2026/Discussion-panel/1.webp",
    description: "",
    event: "Annual Meet 2026",
  },
  {
    src: "assets/images/gallery/year-2026/Keynote-speaker/2.webp",
    description: "",
    event: "Annual Meet 2026",
  },  
  
  {
    src: "assets/images/event-banner/meetup-2026-img4.png",
    description: "",
    event: "Annual Meet 2026",
  },
  {
    src: "assets/images/gallery/year-2026/Food/12.webp",
    description: "",
    event: "Annual Meet 2026",
  },
];

// Set to true to display the Summer Meet 2026 tab and its photos
const ENABLE_SUMMER_MEET = true;

const PhotoGallery = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPending, startTransition] = useTransition();
  const [activeEvent, setActiveEvent] = useState(ENABLE_SUMMER_MEET ? "Summer Meet 2026" : "Annual Meet 2026");

  const availableEvents = ENABLE_SUMMER_MEET 
    ? ["Summer Meet 2026", "Annual Meet 2026"] 
    : ["Annual Meet 2026"];

  const filteredPhotos = homeImageData.filter(
    (photo) => photo.event === activeEvent
  );

  // Animation variants synced with the ultra-smooth 2026 version
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.98, y: 15 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  const openModal = useCallback((index) => {
    startTransition(() => {
      setCurrentIndex(index);
      setModalOpen(true);
    });
  }, []);

  const closeModal = useCallback(() => {
    startTransition(() => setModalOpen(false));
  }, []);

  const showNext = useCallback(() => {
    startTransition(() => {
      setCurrentIndex((prev) => (prev + 1) % filteredPhotos.length);
    });
  }, [filteredPhotos.length]);

  const showPrev = useCallback(() => {
    startTransition(() => {
      setCurrentIndex((prev) => (prev - 1 + filteredPhotos.length) % filteredPhotos.length);
    });
  }, [filteredPhotos.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!modalOpen) return;
      if (e.key === "ArrowRight") showNext();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, showNext, showPrev, closeModal]);

  return (
    <section className="photo_gallery_section section_space bg-white">
      <div className="container">
        <div className="heading_block text-center mb-5">
          <div className="badge style_2 text-uppercase mb-3">
            <span>
              <i className="fa-solid fa-circle text-primary"></i> Our Gallery{" "}
              <i className="fa-solid fa-circle text-primary"></i>
            </span>
          </div>
          <h2 className="heading_text mb-0">Photo Gallery 2026</h2>
        </div>

        {availableEvents.length > 1 && (
          <div className="event-tabs-wrapper mb-10 text-center">
            <div className="event-tabs-pill">
              {availableEvents.map((evt) => (
                <button
                  key={evt}
                  className={`event-tab-btn ${activeEvent === evt ? "active" : ""}`}
                  onClick={() => {
                    setActiveEvent(evt);
                  }}
                >
                  {evt}
                </button>
              ))}
            </div>
          </div>
        )}

        <motion.div 
          className="row g-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          key={activeEvent}
        >
          {filteredPhotos.map((image, index) => (
            <motion.div 
              key={index}
              className="col-lg-3 col-md-6 col-sm-6"
              variants={itemVariants}
            >
              <div className="gallery_item_card" onClick={() => openModal(index)}>
                <div className="img_wrap">
                  <img src={image.src} alt={image.description} loading="lazy" />
                  <div className="hover_overlay">
                    <i className="fa-solid fa-plus"></i>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
          {filteredPhotos.length === 0 && (
            <div className="col-12 text-center py-4">
              <p className="no_photos_text text-muted">Coming soon.</p>
            </div>
          )}
        </motion.div>

        <div className="text-center mt-5">
          <NavLink className="btn bg-primary text-dark fw-bold px-5" to="/photos-gallery-2026">
            <span className="btn_label">View All Photos</span>
          </NavLink>
        </div>
      </div>

      {/* Lightbox Modal - Ported from 2026 Engine */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div 
            className="gallery_lightbox_overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div 
              className="lightbox_content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="lightbox_main">
                <div className="img_container">
                  <div className="img_relative_wrap">
                    <button className="nav_btn prev" onClick={showPrev} aria-label="Previous image">
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    
                    <img src={filteredPhotos[currentIndex]?.src} alt="Gallery Full" />
                    
                    <button className="nav_btn next" onClick={showNext} aria-label="Next image">
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>

                    <button className="close_btn" onClick={closeModal} aria-label="Close modal">
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                  {filteredPhotos[currentIndex]?.description && (
                    <p className="img_caption">{filteredPhotos[currentIndex]?.description}</p>
                  )}
                </div>
              </div>

              <div className="lightbox_footer">
                <span className="count_text">
                  {currentIndex + 1} / {filteredPhotos.length}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PhotoGallery;